import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import Modal from '../components/Modal';
import { Star, Settings, Trash2, UserPlus } from 'lucide-react';
import { doc, getDoc, collection, getDocs, query, orderBy, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';

const ConferenceOverview = () => {
  const { conferenceId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  const [conference, setConference] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStarred, setIsStarred] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchConferenceData = async () => {
      try {
        // Fetch conference data
        const conferenceRef = doc(db, 'conferences', conferenceId);
        const conferenceSnap = await getDoc(conferenceRef);
        
        if (!conferenceSnap.exists()) {
          console.error('Conference not found');
          setLoading(false);
          return;
        }

        const conferenceData = {
          id: conferenceSnap.id,
          ...conferenceSnap.data(),
          startDate: conferenceSnap.data().startDate?.toDate() || null,
          endDate: conferenceSnap.data().endDate?.toDate() || null,
          date: conferenceSnap.data().date?.toDate() || conferenceSnap.data().startDate?.toDate() || null
        };
        setConference(conferenceData);
        setIsStarred(conferenceSnap.data().starred || false);

        // Fetch leads from subcollection
        const leadsRef = collection(db, 'conferences', conferenceId, 'leads');
        const leadsQuery = query(leadsRef, orderBy('date', 'desc'));
        const leadsSnapshot = await getDocs(leadsQuery);
        
        const leadsData = leadsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || doc.data().createdAt?.toDate() || null
        }));
        
        setLeads(leadsData);
      } catch (error) {
        console.error('Error fetching conference data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (conferenceId) {
      fetchConferenceData();
    }
  }, [conferenceId]);

  const handleStarClick = async () => {
    try {
      const newStarredState = !isStarred;
      setIsStarred(newStarredState);
      
      // Update in Firestore
      const conferenceRef = doc(db, 'conferences', conferenceId);
      await updateDoc(conferenceRef, {
        starred: newStarredState
      });
    } catch (error) {
      console.error('Error updating star status:', error);
      // Revert state on error
      setIsStarred(!isStarred);
    }
  };

  const handleRowClick = (lead) => {
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
  };

  const handleDeleteLead = async () => {
    if (!selectedLead || !selectedLead.id) return;

    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const leadRef = doc(db, 'conferences', conferenceId, 'leads', selectedLead.id);
      await deleteDoc(leadRef);
      
      // Update local state
      setLeads(leads.filter(lead => lead.id !== selectedLead.id));
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMakeLead = async () => {
    // TODO: Implement make lead functionality
    console.log('Make lead clicked for:', selectedLead);
    alert('Make Lead functionality to be implemented');
  };

  if (loading) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <BackButton />
          <div className="text-center mt-8">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading conference...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!conference) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <BackButton />
          <div className="text-center mt-8">
            <p className="text-gray-600 dark:text-gray-400">Conference not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <BackButton />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">
            {conference.name}
          </h1>
          <div className="w-24 flex items-center justify-end gap-3">
            <button
              onClick={() => navigate(`/crm/conferences-reports/${conferenceId}/spinwheel`)}
              className="transition-colors"
              aria-label="Open spin wheel"
            >
              <Settings className="h-6 w-6 text-red-600 hover:text-red-700" />
            </button>
            <button
              onClick={handleStarClick}
              className="transition-colors"
              aria-label={isStarred ? 'Unstar conference' : 'Star conference'}
            >
              <Star
                className={`h-6 w-6 transition-colors ${
                  isStarred
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-400 hover:text-yellow-400'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    First Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Last Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Company Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Booked
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No leads found for this conference
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => {
                    const isBooked = lead.booked || lead.isBooked || lead.bookedStatus === true || lead.bookedStatus === 'yes' || lead.bookedStatus === 'Yes';
                    return (
                      <tr 
                        key={lead.id}
                        onClick={() => handleRowClick(lead)}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {lead.firstName || lead.first_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {lead.lastName || lead.last_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {lead.email || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {lead.phone || lead.phoneNumber || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {lead.companyName || lead.company || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {lead.date ? lead.date.toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {isBooked ? 'Yes' : 'No'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lead Details Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="lg"
        >
          {selectedLead && (
            <div className="p-6">
              <div className="flex items-center justify-center mb-6 -mt-2 relative">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Lead Details
                </h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      First Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedLead.firstName || selectedLead.first_name || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Last Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedLead.lastName || selectedLead.last_name || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Email
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedLead.email || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Phone
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedLead.phone || selectedLead.phoneNumber || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Company Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedLead.companyName || selectedLead.company || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Date
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedLead.date ? selectedLead.date.toLocaleDateString() : '-'}
                    </p>
                  </div>
                </div>

                {/* Additional Information section - under Company Name */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Additional Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Booked:
                      </span>
                      <span className="text-sm text-gray-900 dark:text-white">
                        {(selectedLead.booked || selectedLead.isBooked || selectedLead.bookedStatus === true || selectedLead.bookedStatus === 'yes' || selectedLead.bookedStatus === 'Yes') ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {Object.entries(selectedLead)
                      .filter(([key]) => 
                        !['id', 'firstName', 'first_name', 'lastName', 'last_name', 'email', 'phone', 'phoneNumber', 'companyName', 'company', 'date', 'booked', 'isBooked', 'bookedStatus', 'createdAt'].includes(key)
                      )
                      .map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className="text-sm text-gray-900 dark:text-white">
                            {value instanceof Date ? value.toLocaleDateString() : String(value || '-')}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
                  <button
                    onClick={handleDeleteLead}
                    disabled={isDeleting}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    {isDeleting ? 'Deleting...' : 'Delete Lead'}
                  </button>
                  <button
                    onClick={handleMakeLead}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <UserPlus className="h-4 w-4" />
                    Make Lead
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ConferenceOverview;

